<template>
  <div class="general-report">
    <v-container fluid class="pa-0">
      <v-layout row wrap>
        <!-- Barra lateral -->
        <v-flex md3 xs12 class="pa-2">
          <v-card>
            <!-- Lista de informações -->
            <v-list dense>
              <!-- Dados do paciente -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="green">fas fa-user</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ patientName }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ patientBirthdate }} - Patid: {{ patientId }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover && canEditReport">
                      <v-btn icon @click="dialogPatient = !dialogPatient">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>

              <v-divider inset></v-divider>

              <!-- Status e AN -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="blue">fas fa-file-medical</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ studyTitle || 'Laudo Geral' }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ an }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover && canEditReport">
                      <v-btn icon @click="onOpenEdit">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>

              <v-divider inset></v-divider>

              <!-- Data do exame -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="pink">fas fa-calendar</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ serviceDate }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ status }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover && canEditReport">
                      <v-btn icon @click="onOpenEdit">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>

              <v-divider inset></v-divider>

              <!-- Médico executor -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="purple">fas fa-user-md</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">{{ doctorName }}</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ doctorCRM }}</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover && canEditReport">
                      <v-btn icon @click="onOpenEdit">
                        <v-icon small color="primary">edit</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>

              <v-divider inset></v-divider>

              <!-- Seção de imagens -->
              <v-hover>
                <v-list-tile slot-scope="{ hover }">
                  <v-list-tile-avatar>
                    <v-icon color="green">fas fa-images</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title class="list-dense">Imagens</v-list-tile-title>
                    <small class="grey--text text--darken-1">{{ listImageURL.length }} imagens</small>
                  </v-list-tile-content>
                  <v-fade-transition>
                    <v-list-tile-action v-if="hover && hasImages">
                      <v-btn icon :to="`/viewer/${gestationInteraction.iuid}`" target="_blank">
                        <v-icon small color="primary">open_in_new</v-icon>
                      </v-btn>
                    </v-list-tile-action>
                  </v-fade-transition>
                </v-list-tile>
              </v-hover>
            </v-list>

            <!-- Container de imagens -->
            <div class="images-container" v-if="hasImages">
              <v-container class="scroll-y pa-2" id="scroll-target">
                <v-flex v-for="(video, i) in listVideo" :key="'v'+i" xs12 class="pa-1">
                  <iframe
                    :src="video"
                    width="100%"
                    height="150"
                    style="border: 0"
                    allowfullscreen/>
                </v-flex>
                <v-flex v-for="(image, i) in listImageURL" :key="'i'+i" xs12 class="pa-1">
                  <v-hover>
                    <v-img
                      slot-scope="{ hover }"
                      :src="image"
                      height="150"
                      @click="slideIndex = i"
                      :class="{'elevation-4': hover}"
                      class="image-thumb">
                      <v-fade-transition>
                        <v-overlay
                          v-if="hover"
                          absolute
                          color="white"
                          opacity="0.7">
                          <v-icon color="primary" size="36">search</v-icon>
                        </v-overlay>
                      </v-fade-transition>
                    </v-img>
                  </v-hover>
                </v-flex>
              </v-container>
              <vue-gallery :images="listImageURL" :index="slideIndex" @close="slideIndex = null"></vue-gallery>
            </div>
          </v-card>
        </v-flex>

        <!-- Área principal -->
        <v-flex md9 xs12 class="pa-2">
          <v-card class="report-card">
            <div class="iframe-container">
              <v-alert
                :value="!canEditReport && gestationInteraction.status !== 'PUBLICADO'"
                type="warning"
              >
                {{ $t('messages.reportNotPublished') }}
              </v-alert>
              <iframe class="iframe-pdf" frameborder="0" ref="frame" :src="urlReport" @load="load" v-show="shouldShowPdf"></iframe>
              <u-form
                v-model="resultForm"
                flex
                :id="form.key"
                :json="form.components[0]"
                v-if="shouldShowForm"
              ></u-form>
              <div class="loading" v-if="loading">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  :size="50"
                ></v-progress-circular>
            </div>
            </div>

            <v-card-actions>
              <!-- Botões -->
              <v-layout justify-center class="button-container pa-2">
                <input type="file" ref="pdfInput" accept=".pdf" style="display: none" @change="onFileSelected">
                <v-btn @click="$refs.pdfInput.click()" v-if="canEditReport && !initProcess">Anexar PDF</v-btn>
                <v-btn @click="dialogPresetPrint = true" v-if="!initProcess && canEditReport">alterar parametros</v-btn>
                <v-btn @click="editarLaudo" v-show="loaded && canEditReport">editar</v-btn>
                <v-btn @click="updatePresets" v-if="!initProcess && canEditReport">gerar laudo</v-btn>
                <v-btn @click="publicar" color="success" v-show="initProcess && canPublishReport">publicar</v-btn>
                <dialog-preset-print ref="dialogPresetPrint" v-model="dialogPresetPrint" :id="this.id" reportType="General" @apply="gerarLaudo"></dialog-preset-print>
              </v-layout>
            </v-card-actions>

          </v-card>


        </v-flex>
      </v-layout>
    </v-container>
    <UfPatient v-model="dialogPatient" :iuid="patient.iuid"/>
    <UfEditExamDetail v-model="dialogEdit" :iuid="itemSelect"/>
  </div>
</template>

<script>
import moment from 'moment'
import VueGallery from 'vue-gallery'
import axios from 'axios'
import _ from 'lodash'
import {mapState, mapGetters} from 'vuex'
import dialogPresetPrint from '@/pages/ultra-fetal/summary/components/dialogPresetPrint'
import UForm from '@/components/form/form'
import { db, storageReports } from '@/store/firebase'
import formsJSON from '@/../functions/shared/forms.json'
import apiGestation from '@/api/gestation'
import UfPatient from '@/components/ultra-fetal/home/Patient'
import UfEditExamDetail from '@/components/util/EditExam'
import roleTypes from '@/utils/roleTypes'

const getImageURL = 'https://app.ultrahealth.care/functions/healthcareImage?id='
const URL_BASE_REPORT = 'https://report-server-dot-ultracarebr.appspot.com/report/'

export default {
  name: 'GeneralReport',
  components: {
    dialogPresetPrint,
    VueGallery,
    UForm,
    UfPatient,
    UfEditExamDetail
  },
  props: {
    id: {
      required: false
    }
  },
  computed: {
    ...mapState({
      gestationInteraction: (state) => state.ultraFetal.gestationInteraction,
      user: (state) => state.user,
      dicomParams: state => state.ultraFetal.dicomParams,
      patient: state => state.ultraFetal.patient
    }),
    ...mapGetters('formCustomization', ['getCustomizedForms', 'getFormByKey']),
    listImageURL () {
      return this.dicomParams.map(item => getImageURL + item.iuid)
    },
    form () {
      return this.getFormByKey('conclusion')
    },
    serviceDate () {
      return this.gestationInteraction && this.gestationInteraction.date ? moment(this.gestationInteraction.date).format('DD/MM/YYYY') : '--/--/----'
    },
    status () {
      return this.gestationInteraction && this.gestationInteraction.status ? this.gestationInteraction.status : '--'
    },
    an () {
      return this.gestationInteraction && this.gestationInteraction.forms && this.gestationInteraction.forms['gestationalAge.info.accessionNumber'] ? this.gestationInteraction.forms['gestationalAge.info.accessionNumber'] : ''
    },
    patientName () {
      return this.patient.name ? this.patient.name : ''
    },
    patientId () {
      return this.patient.patId ? this.patient.patId : ''
    },
    patientBirthdate () {
      return this.patient.birthdate ? moment(this.patient.birthdate).format('DD/MM/YYYY') : '--/--/----'
    },
    patientSex () {
      return this.patient.sex ? this.patient.sex : ''
    },
    urlReport () {
      if (!this.initProcess) return ''
      const { iuid } = this.gestationInteraction
      if (!iuid) return ''
      return URL_BASE_REPORT + iuid
    },
    hasImages () {
      return this.listImageURL.length > 0 || this.listVideo.length > 0
    },
    studyTitle () {
      if (this.gestationInteraction && this.gestationInteraction.studies) {
        const studies = this.gestationInteraction.studies
        const firstStudy = Object.values(studies)[0]
        return firstStudy ? firstStudy.title : null
      }
      return null
    },
    doctorName () {
      if (this.gestationInteraction && this.gestationInteraction.doctor) {
        return this.gestationInteraction.doctor.name || this.gestationInteraction.resource || '--'
      }
      return '--'
    },
    doctorCRM () {
      if (this.gestationInteraction && this.gestationInteraction.doctor) {
        return this.gestationInteraction.doctor.identity ? `CRM: ${this.gestationInteraction.doctor.identity}` : ''
      }
      return ''
    },
    canEditReport () {
      // Admins e gerentes sempre podem editar
      if ([roleTypes.admin, roleTypes.manager].includes(this.user.role)) {
        return true
      }

      // Médicos podem editar se:
      if (roleTypes.doctor === this.user.role) {
        // 1. O exame não tem médico associado (o médico atual pode "assumir" o exame)
        if (!this.gestationInteraction.doctor) {
          return true
        }

        // 2. O médico atual é o médico associado ao exame
        if (this.gestationInteraction.doctor && this.gestationInteraction.doctor.id === this.user.uid) {
          return true
        }
      }

      // Outros casos não podem editar
      return false
    },
    shouldShowPdf () {
      return (
        (this.loaded && this.initProcess) || // Para usuários com permissão que geraram o laudo ou anexaram PDF
        (this.gestationInteraction.status === 'PUBLICADO') // Para qualquer laudo publicado
      )
    },
    canViewReport () {
      return this.canEditReport ||
             this.gestationInteraction.status === 'PUBLICADO'
    },
    canPublishReport () {
      // Verifica se é admin ou manager
      const isAdminOrManager = [roleTypes.admin, roleTypes.manager].includes(this.user.role)

      // Verifica se é o médico responsável
      const isResponsibleDoctor = (
        roleTypes.doctor === this.user.role &&
        this.gestationInteraction.doctor &&
        this.gestationInteraction.doctor.id === this.user.uid
      )

      // Verifica se tem permissão e se o status está em andamento
      return (isAdminOrManager || isResponsibleDoctor) &&
             this.gestationInteraction.status === 'ANDAMENTO'
    },
    shouldShowForm () {
      return this.gestationInteraction &&
             this.gestationInteraction.status !== 'PUBLICADO' &&
             this.canEditReport &&
             !this.initProcess
    },
    forms () {
      return this.getCustomizedForms
    }
  },

  watch: {
    resultForm: {
      handler: function () {
        this.save()
      },
      deep: true
    },
    'gestationInteraction.forms' (val) {
      if (!_.isEqual(val, this.resultForm)) {
        this.resultForm = val
      }
    },
    'gi.print' (val) {
      // this.gerarLaudo()
    }
  },

  data () {
    return {
      dialogPresetPrint: false,
      dialogPatient: false,
      dialogEdit: false,
      itemSelect: '',
      listVideo: [],
      slideIndex: null,
      formsJSON: formsJSON,
      resultForm: {},
      loaded: false,
      initProcess: false,
      loadProgress: 0,
      interval: {},
      firstTime: true,
      pdfAnexado: false,
      loading: false
    }
  },
  async mounted () {
    await this.$store.dispatch('ultraFetal/getGestationInteraction', this.id).then(data => {
      if (!this.dicomParams || !this.dicomParams.iuid) {
        if (data.studyDate) {
          this.$store.dispatch('ultraFetal/getDicomParams', { identity: data.company.identity, patId: data.patient.patId, date: data.date, studyDate: data.studyDate })
        } else {
          this.$store.dispatch('ultraFetal/getDicomParams', { identity: data.company.identity, patId: data.patient.patId, date: data.date })
        }
      }
    })
    await this.$store.dispatch('ultraFetal/getPatient', this.gestationInteraction.patient.id)
    await this.axiosGet()
    this.resultForm = Object.assign({}, this.gestationInteraction.forms)

    // Inicializar visualização do PDF para usuários sem permissão
    if (this.gestationInteraction.status === 'PUBLICADO') {
      this.initProcess = true
      this.startProgress()
      this.loaded = true
      return
    }
    if (this.gestationInteraction) {
      await this.verifyInterationDate()
      if (this.canEditReport) {
        await this.verifyDoctor()
      }
    }
  },

  methods: {
    verifyInterationDate () {
      if (!this.resultForm['gestationalAge.info.interationDate']) {
        this.resultForm['gestationalAge.info.interationDate'] = this.gestationInteraction.date
      }
    },
    axiosGet () {
      axios.get('https://us-central1-ultracarebr.cloudfunctions.net/patientPortal?id=' + this.id)
       .then(res => {
         this.listVideo = res.data.videosList
       })
      .catch(err => {
        console.error(err)
      })
    },
    save: _.debounce(function () {
      // Não deve salvar se não tiver permissão para editar
      if (!this.canEditReport) {
        return
      }

      // Se o status não for ANDAMENTO e houver qualquer alteração no formulário
      if (this.gestationInteraction.status !== 'ANDAMENTO') {
        const formChanged = !_.isEqual(this.gestationInteraction.forms, this.resultForm)
        if (formChanged) {
          this.saveStatus('ANDAMENTO')
        }
      }
      let form = this.resultForm
      Object.entries(form).forEach(([key, value]) => {
        if ([undefined, null, ''].indexOf(value) > -1) delete form[key]
      })
      return db.doc('gestationInteraction/' + this.gestationInteraction.iuid).update({forms: form})
    }, 1000),
    async publicar () {
      if (!this.gestationInteraction.patient || !this.gestationInteraction.patient.patId) {
        alert('Não é possível publicar exames sem Código (PatId) de paciente. Favor preencher no cadastro do Paciente')
        return
      }
      if (!this.gestationInteraction.forms || !this.gestationInteraction.forms['gestationalAge.info.accessionNumber']) {
        if (!this.gestationInteraction.studiesList && !this.gestationInteraction.studies) {
          alert('Não é possível publicar exames sem Código de exame (AN). Favor preencher no formulário "Informações"')
          return
        } else this.insertAccessionNumberFromStudies()
      }
      this.insertStudyType()

      // Se houver um PDF anexado, faz o upload
      if (this.pdfAnexado) {
        this.loading = true // Era uploading
        const file = this.$refs.pdfInput.files[0]
        const storageRef = storageReports.ref(`${this.gestationInteraction.iuid}.pdf`)
        try {
          await storageRef.put(file)
          console.log('PDF uploaded successfully!')
        } catch (error) {
          console.error('Error uploading PDF:', error)
          alert('Erro ao fazer upload do PDF.')
          this.loading = false
          return
        }
        this.loading = false
      }

      this.saveStatus('PUBLICADO').then(() => {
        this.$router.push('/studies')
      })
    },
    insertAccessionNumberFromStudies () {
      Object.assign(this.resultForm, {'gestationalAge.info.accessionNumber': Object.keys(this.gestationInteraction.studies)[0]})
      db.doc('gestationInteraction/' + this.gestationInteraction.iuid)
        .update({ forms: this.resultForm }).then(() => {
          this.gestationInteraction.forms['gestationalAge.info.accessionNumber'] = Object.keys(this.gestationInteraction.studies)[0]
        })
    },
    insertStudyType () {
      if (this.resultForm) {
        this.resultForm['studyType'] = 'Laudo Geral'
        db.doc('gestationInteraction/' + this.gestationInteraction.iuid)
        .update({ forms: this.resultForm }).then(() => {
          this.gestationInteraction.forms['studyType'] = 'Laudo Geral'
        })
      }
    },
    saveStatus: function (status) {
      return db.doc('gestationInteraction/' + this.gestationInteraction.iuid)
        .update({ status }).then(() => {
          this.gestationInteraction.status = status
        })
    },
    startProgress () {
      this.interval = setInterval(() => {
        if (this.loadProgress === 100 || this.loaded === true) {
          clearInterval(this.interval)
        }
        this.loadProgress += 2
      }, 1000)
    },
    load () {
      if (!this.gestationInteraction.iuid) return
      if (this.initProcess || (!this.canEditReport && this.gestationInteraction.status === 'PUBLICADO')) {
        this.loaded = true
        this.loading = false
        clearInterval(this.interval)
        this.loadProgress = 100
      }
    },
    updatePresets () {
      this.$refs.dialogPresetPrint.save()
    },
    gerarLaudo () {
      // Se um PDF foi anexado, remove o anexo
      if (this.pdfAnexado) {
        this.pdfAnexado = false
      }

      this.loadProgress = 0
      this.loaded = false
      this.initProcess = true
      this.loading = true

      setTimeout(() => {
        if (this.initProcess) {
          if (this.$refs.frame) {
            this.$refs.frame.src = this.urlReport
          }
        }
        this.startProgress()
      }, 2000)
    },
    editarLaudo () {
      this.loadProgress = 0
      this.initProcess = false
      this.loaded = false
      apiGestation.update({
        iuid: this.gestationInteraction.iuid,
        status: 'ANDAMENTO'
      })
    },
    verifyDoctor () {
      // Verifica se o usuário é médico e se o médico não está preenchido
      if (this.user.role !== 'doctor') return
      const updateData = {}
      if (!this.gestationInteraction.doctor || !this.gestationInteraction.doctor.name) {
        const doctor = {
          id: this.user.iuid,
          identity: this.user.crm,
          name: this.user.displayName
        }
        this.gestationInteraction.doctor = doctor
        updateData.doctor = doctor
      }
      // Verifica se o recurso não está preenchido
      if (!this.gestationInteraction.resource) {
        const resource = this.user.displayName
        this.gestationInteraction.resource = resource
        updateData.resource = resource
      }
      if (updateData.doctor || updateData.resource) {
        db.doc('gestationInteraction/' + this.gestationInteraction.iuid).update(updateData)
      }
    },
    onOpenEdit () {
      this.itemSelect = this.gestationInteraction.iuid
      this.dialogEdit = true
    },
    onFileSelected (event) {
      const file = event.target.files[0]
      if (file) {
        this.pdfAnexado = true
        this.gestationInteraction.status = 'ANDAMENTO'
        this.initProcess = true // Inicia o processo para exibir o PDF
        this.$nextTick(() => {
          this.$refs.frame.src = URL.createObjectURL(file)
          this.loaded = true // Define loaded como true para mostrar o iframe
        })
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.general-report
  display: flex
  flex-direction: column

.report-card
  height: calc(100vh - 150px)
  margin: 0

.iframe-container
  height: 100%
  width: 100%

.images-container
  height: calc(100vh - 300px)
  overflow-y: auto

.image-thumb
  cursor: pointer
  transition: all .2s ease-in-out
  background-color: #f5f5f5
  border-radius: 4px

  &:hover
    transform: scale(1.02)
    background-color: white
    box-shadow: 0 2px 8px rgba(0,0,0,0.1)

.iframe-pdf
  height: 100%
  width: 100%

.loading
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)

.list-dense
  height: 18px
  line-height: 24px

.button-container
  background-color: white
</style>



// WEBPACK FOOTER //
// src/pages/GeneralReport.vue